/* eslint-disable react/prop-types */
/**
 * Pass F-UI — AirframeAeroBadge.
 *
 * Two-state pill that flags whether an FDM has per-airframe
 * aerodynamic coefficients calibrated (post Pass D-lite data closure
 * pack v7) or whether it falls back to the kinematic-with-mass
 * stepper. Defence palette only: teal accent for "6-DoF aero",
 * steel-gray for "Kinematic + mass". UK spelling throughout.
 *
 * The badge is decoration — it does NOT toggle the underlying
 * engagement behaviour. That is determined server-side by the
 * scenario's airframe selection feeding into
 * services.phase19_multiagent_engine via the FDM registry. The badge
 * tells the scenario author up-front what physics the engagement
 * will run.
 */
import { useMemo } from "react";

import { DEFENCE_PALETTE } from "@/components/phase19/placementUtils";

const STATES = Object.freeze({
    calibrated: Object.freeze({
        label: "6-DoF aero",
        background: DEFENCE_PALETTE.teal,
        // Navy on teal carries enough contrast (WCAG AAA at 14px) so
        // we keep the defence palette discipline intact.
        foreground: DEFENCE_PALETTE.navy,
        tooltip:
            "Per-airframe aerodynamic coefficients calibrated; "
            + "engagements run full 6-DoF JSBSim physics.",
    }),
    kinematic: Object.freeze({
        label: "Kinematic + mass",
        background: DEFENCE_PALETTE.steelGray,
        foreground: "#FFFFFF",
        tooltip:
            "No per-airframe aerodynamic coefficients yet; "
            + "engagements run kinematic stepper using FDM mass and "
            + "drag coefficient. Per-airframe differentiation is via "
            + "mass only.",
    }),
});

export default function AirframeAeroBadge({
    aeroCalibrated,
    className = "",
    title,
}) {
    const state = useMemo(
        () => (aeroCalibrated ? STATES.calibrated : STATES.kinematic),
        [aeroCalibrated],
    );
    const testId = aeroCalibrated
        ? "airframe-aero-badge-calibrated"
        : "airframe-aero-badge-kinematic";
    return (
        <span
            className={
                `inline-flex items-center rounded-full px-2 py-0.5 `
                + `text-[10px] font-medium uppercase tracking-wide `
                + `leading-none ${className}`
            }
            style={{
                background: state.background,
                color: state.foreground,
                fontVariantNumeric: "tabular-nums",
            }}
            data-testid={testId}
            data-aero-calibrated={String(Boolean(aeroCalibrated))}
            title={title ?? state.tooltip}
            aria-label={state.label}
        >
            {state.label}
        </span>
    );
}

export { STATES as AERO_BADGE_STATES };
